import React, { useState, useEffect, useContext } from 'react';
import { Card, Table, NavDropdown } from 'react-bootstrap';
import Chart from 'react-apexcharts';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import InvestmentDot from './InvestmentDot';
import { useWidgetContext } from '../context/DragContext';
import { AuthContext } from '../context/AuthContext';
import SwitchAccount from '../common/SwitchAccount';
import Skeleton from 'react-loading-skeleton';

function AccountOverview() {
  const [sort, setSort] = React.useState('category');
  const { removeWidgetFromGrid } = useWidgetContext();
  const [showDropdown, setShowDropdown] = useState(false);
  const { currentDate, currentTime, accountInfoData, yourHoldingData, userData, accountLoading } = useContext(AuthContext);
  const [investmentData, setInvestmentData] = useState([]);
  const [totalRealizedPnl, setTotalRealizedPnl] = useState();
  const [avgPnlPercentage, setAvgPnlPercentage] = useState();
  const [switchAccount, setSwitchAccount] = useState(false);

  const accruedCashAmount = accountInfoData?.summary?.netLiquidationValue|| 0;
  const availableFunds = accountInfoData?.summary?.availableFunds || 0;

  const handleOpenSwitchAccount = () => setSwitchAccount(true);
  const handleCloseSwitchAccount = () => setSwitchAccount(false);

  const colorMapping = {
    "GC": "#317CFF",
    "SI": "#E04662",
    "ZC": "#F4F1ED",
    "AAPL": "#FF5733",
    "CL": "#40C796",
    "ZS": "#FBD914",
  };

  const generateRandomColor = () => {
    return `#${Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0')}`;
  };

  const getColor = (name) => {
    if (!colorMapping[name]) {
      if (Object.keys(colorMapping).length < 15) {
        colorMapping[name] = generateRandomColor();
      } else {
        return "#000000"; // Default color if the limit is reached
      }
    }
    return colorMapping[name];
  };

  useEffect(() => {
    if (Array.isArray(yourHoldingData) && yourHoldingData.length > 0) {
      const totalInvestment = yourHoldingData.reduce((sum, item) => sum + (item.marketValue || 0), 0);
      
      // Calculate total unrealized P/L
      const totalRealizPnl = yourHoldingData.reduce((sum, item) => sum + (item.realizedPnl || 0), 0);
      
      // Divide unrealized P/L by 3
      setTotalRealizedPnl(totalRealizPnl.toFixed(2));

      // Calculate the weighted average P/L percentage
      let averagePnlPercentage;

      if (accruedCashAmount === 0) {
        averagePnlPercentage = 0;
      } else {
        averagePnlPercentage = ((totalRealizPnl / accruedCashAmount) * 100);
        
        // Check for NaN or Infinity and handle them
        if (isNaN(averagePnlPercentage) || !isFinite(averagePnlPercentage)) {
          averagePnlPercentage = 0; // Set to 0 if result is NaN or Infinity
        } else {
          averagePnlPercentage = averagePnlPercentage.toFixed(2); // Format the result to 2 decimal places
        }
      }

      // Set the average P/L percentage
      setAvgPnlPercentage(averagePnlPercentage);
      
      const formattedData = yourHoldingData.map((item) => {
        const percentage = totalInvestment > 0 ? ((item.marketValue / totalInvestment) * 100).toFixed(2) : 0;
        const shortname = item.description.split(" ")[0];
        let name = "";
        if(userData.integrations.current_integration === 'IBKR') {
          let nameParts = item.description.trim().split(/\s+/); // split by any whitespace
          name = nameParts.length > 1 ? `${nameParts[0]}-${nameParts[nameParts.length - 1]}` : nameParts[0];
        } else {
          name = item.description.split(" ")[0];
        }
        return {
          name,
          percentage,
          color: getColor(shortname),
        };
      });
      setInvestmentData(formattedData);
    }
  }, [yourHoldingData, accruedCashAmount]);

  const handleChange = (event) => {
    const selectedSort = event.target.value;
    setSort(selectedSort);
  
    // Sorting logic
    const sortedData = [...investmentData];
    if (selectedSort === 'category') {
      sortedData.sort((a, b) => a.name.localeCompare(b.name));
    } else if (selectedSort === 'amount' || selectedSort === 'performance') {
      sortedData.sort((a, b) => b.percentage - a.percentage);
    }
    setInvestmentData(sortedData);
  };

  const handleToggle = (isOpen) => {
    setShowDropdown(isOpen);
  };

  const handleSvgClick = () => {
    setShowDropdown(!showDropdown);
  };

  const chartOptions = {
    series: investmentData.map(item => parseFloat(item.percentage)),
    options: {
      chart: {
        type: 'donut',
        height: '100%', // Make the chart responsive to the container height
      },
      labels: investmentData.map(item => item.name),
      colors: investmentData.map(item => item.color),
      stroke: {
        show: true,
        width: 0,
        colors: ['transparent'],
      },
      legend: {
        show: false,
      },
      dataLabels: {
        enabled: false,
      },
    },
  };

  const Loader = ({ width = 80 }) => (
    <Skeleton
      width={width}
      style={{ margin: "4px 2px", borderRadius: "3px" }}
      baseColor="rgba(137, 136, 137, 0.3)"
      highlightColor="rgba(176, 176, 176, 0.3)"
    />
  );

  return (
    <>
      <Card className={`main-card mt-3 ${userData?.added_integrations?.length === 0 ? 'blur-card' : ''}`}>
        {userData?.added_integrations?.length === 0 && (
          <div className="blur-overlay">
            <img
              src={`${process.env.PUBLIC_URL}/images/lock-icon.svg`}
              alt="Lock Icon"
              style={{ height: '20px', width: '16px' }}
              className="lock-icon"
            />
            {userData?.added_integrations?.length === 0 && <button className="subscribe-button" onClick={handleOpenSwitchAccount}>Click to add Provider</button>}
          </div>
        )}
        <Card.Header className="pb-0">
          <div className="d-flex justify-content-between mt-1">
            <h2 className="medium-heading mb-0">Account Overview</h2>
            <div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                onClick={handleSvgClick}
                style={{ cursor: 'pointer' }}
              >
                <path
                  d="M12 13.5C11.7033 13.5 11.4133 13.412 11.1666 13.2472C10.92 13.0824 10.7277 12.8481 10.6142 12.574C10.5006 12.2999 10.4709 11.9983 10.5288 11.7074C10.5867 11.4164 10.7296 11.1491 10.9393 10.9393C11.1491 10.7296 11.4164 10.5867 11.7074 10.5288C11.9983 10.4709 12.2999 10.5006 12.574 10.6142C12.8481 10.7277 13.0824 10.92 13.2472 11.1666C13.412 11.4133 13.5 11.7033 13.5 12C13.5 12.3978 13.342 12.7794 13.0607 13.0607C12.7794 13.342 12.3978 13.5 12 13.5Z"
                  fill="white"
                />
                <path
                  d="M18.75 13.5C18.4533 13.5 18.1633 13.412 17.9166 13.2472C17.67 13.0824 17.4777 12.8481 17.3642 12.574C17.2506 12.2999 17.2209 11.9983 17.2788 11.7074C17.3367 11.4164 17.4796 11.1491 17.6893 10.9393C17.8991 10.7296 18.1664 10.5867 18.4574 10.5288C18.7483 10.4709 19.0499 10.5006 19.324 10.6142C19.5981 10.7277 19.8324 10.92 19.9972 11.1666C20.162 11.4133 20.25 11.7033 20.25 12C20.25 12.3978 20.092 12.7794 19.8107 13.0607C19.5294 13.342 19.1478 13.5 18.75 13.5Z"
                  fill="white"
                />
                <path
                  d="M5.25 13.5C4.95333 13.5 4.66332 13.412 4.41665 13.2472C4.16997 13.0824 3.97771 12.8481 3.86418 12.574C3.75065 12.2999 3.72094 11.9983 3.77882 11.7074C3.8367 11.4164 3.97956 11.1491 4.18934 10.9393C4.39912 10.7296 4.66639 10.5867 4.95737 10.5288C5.24834 10.4709 5.54994 10.5006 5.82403 10.6142C6.09812 10.7277 6.33238 10.92 6.49721 11.1666C6.66203 11.4133 6.75 11.7033 6.75 12C6.75 12.3978 6.59197 12.7794 6.31066 13.0607C6.02936 13.342 5.64783 13.5 5.25 13.5Z"
                  fill="white"
                />
              </svg>
              <NavDropdown
                show={showDropdown}
                onToggle={handleToggle}
                title=""
                id="nav-dropdown"
                align="end"
                className="d-inline custom-nav-dropdown"
                style={{ display: showDropdown ? 'block' : 'none' }}
              >
                <NavDropdown.Item className="dropdown-option" onClick={() => removeWidgetFromGrid('accountoverview')}>Close Window</NavDropdown.Item>
              </NavDropdown>
            </div>
          </div>
          <div className="small-gray small-gary-date">{currentDate} | {currentTime}</div>
        </Card.Header>
        <Card.Body className="pt-0 p-2 mt-1 scrollbar-custom">
          <div className="table-responsive scrollbar-custom plr-2 w-t-div">
            <Table hover className="watchlist-table">
              <thead>
                <tr>
                  <th className="gray-text">Available Funds</th>
                  <th className="gray-text">Net Asset Value</th>
                  <th className="text-end gray-text">P/L</th>
                  <th className="text-end gray-text">P/L %</th>
                </tr>
              </thead>
              <tbody>
                {accountLoading || !userData ? (
                  <tr>
                    <td><Loader /></td>
                    <td><Loader /></td>
                    <td className="text-end"><Loader /></td>
                    <td className="text-end"><Loader /></td>
                  </tr>
                ): (userData?.added_integrations?.length ?? 0) === 0 ? (
                  <tr>
                    <td>99,843.40</td>
                    <td>81,770.32</td>
                    <td className="text-success text-end align-middle">2,923.81</td>
                    <td className="text-success text-end align-middle">13.32%</td>
                  </tr>
                ):(
                  <tr>
                    <td>{availableFunds.toFixed(2)}</td>
                    <td>{accruedCashAmount.toFixed(2)}</td>
                    <td className={`text-end align-middle ${totalRealizedPnl > 0 ? 'text-success' : 'text-danger'}`}>{totalRealizedPnl || 0}</td>
                    <td className={`text-end align-middle ${avgPnlPercentage > 0 ? 'text-success' : 'text-danger'}`}>{avgPnlPercentage || 0}%</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          <div className="gray-text mt-1" style={{marginLeft: '8px'}}>Investment</div>
          {accountLoading || !userData ? (
            <>
              <div className="d-flex">
                <Skeleton width={119} height={119}
                  circle baseColor="rgba(137, 136, 137, 0.3)" highlightColor="rgba(176, 176, 176, 0.3)"
                  style={{
                    mask: "radial-gradient(circle, transparent 48%, black 49%)",
                    WebkitMask: "radial-gradient(circle, transparent 48%, black 49%)"
                  }}
                />
                <div>
                  <Skeleton
                    width={180} height={30}
                    style={{ margin: '4px 2px 14px 20px', borderRadius: '3px' }}
                    baseColor="rgba(137, 136, 137, 0.3)"
                    highlightColor="rgba(176, 176, 176, 0.3)"
                  />
                  <Skeleton
                    width={180}
                    style={{ margin: '4px 2px 14px 20px', borderRadius: '3px' }}
                    baseColor="rgba(137, 136, 137, 0.3)"
                    highlightColor="rgba(176, 176, 176, 0.3)"
                  />
                </div>
              </div>
            </>
          ):(
            <div className="row mt-1 justify-content-between">
              <div className="col-4 p-0" style={{ marginLeft: '-35px' }}>
                <Chart
                  options={
                    chartOptions.series && chartOptions.series.length > 0
                      ? chartOptions.options
                      : {
                        ...chartOptions.options,
                        labels: ["No Data"], // Default label
                      }
                  }
                  series={
                    chartOptions.series && chartOptions.series.length > 0
                    ? chartOptions.series
                    : [100] // Default value for "No Data"
                  }
                  type="donut"
                  width="139%"
                />
              </div>
              <div className="col-8">
                <FormControl className="custom-form-control m-0" sx={{ m: 1, minWidth: 120 }}>
                  <Select
                    value={sort}
                    onChange={handleChange}
                    displayEmpty
                    inputProps={{ 'aria-label': 'Without label' }}
                    className="custom-select"
                  >
                    <MenuItem value="category"> Sort by category </MenuItem>
                    <MenuItem value="amount"> Sort by amount </MenuItem>
                    <MenuItem value="performance"> Sort by performance </MenuItem>
                  </Select>
                </FormControl>
                
                <table className="w-100 mt-2">
                <tbody>
                  {investmentData.length > 0 ? (
                    investmentData.map((investment, index) => {
                      // Create rows for two investments
                      if (index % 2 === 0) {
                        return (
                          <tr key={index}>
                            <td>
                              <InvestmentDot color={investment.color} />
                              <span className="gray-text">{investment.name}</span>
                            </td>
                            <td>
                              <span className="bold">{investment.percentage}%</span>
                            </td>
                            {/* Check if there is a next investment to display in the same row */}
                            {investmentData[index + 1] ? (
                            <>
                              <td>
                                <InvestmentDot color={investmentData[index + 1].color} />
                                <span className="gray-text">{investmentData[index + 1].name}</span>
                              </td>
                              <td>
                                <span className="bold">{investmentData[index + 1].percentage}%</span>
                              </td>
                            </>
                            ) : (
                            // If there's no next investment, fill empty cells
                            <>
                              <td></td>
                              <td></td>
                            </>
                            )}
                          </tr>
                        );
                      }
                      return null; // For odd indices, we don't need to render anything
                      })
                        ) : (
                          <tr>
                            <td colSpan="4" className="no-data">No Data Available</td>
                          </tr>
                        )
                      }
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Card.Body>
      </Card>
      
      <SwitchAccount open={switchAccount} onClose={handleCloseSwitchAccount}/>
    </>
  );
}

export default AccountOverview;
